import { CoinIcon } from "@/designSystemIcons";
import {
  type EvolutionFooterDetails,
  formatEvoCost,
} from "@repo/shared/evolutions";
import { useTranslation } from "@repo/shared/i18n";
import { Link2 } from "lucide-react";
import { ReactNode } from "react";

import { useTokenCosts } from "./useTokenCosts";

const FooterStat = ({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) => (
  <div className="flex flex-1 flex-col items-center gap-y-0.5">
    <span className="text-tiny uppercase text-foreground-muted">{label}</span>
    <span className="flex items-center gap-x-1 font-oswald text-xs font-semibold text-foreground">
      {children}
    </span>
  </div>
);

/** Port of the app's EvolutionFooter: chain length, coin and token cost, games. */
export const EvolutionFooter = ({
  chain,
  coins,
  tokens,
  games,
  assetsBaseUrl,
}: EvolutionFooterDetails & { assetsBaseUrl: string }) => {
  const { t } = useTranslation(["evolutions", "common"]);
  const tokenCosts = useTokenCosts(tokens);
  return (
    <div className="mt-2 flex w-full items-stretch border-t border-edge-strong pt-2 [&>*+*]:border-l [&>*+*]:border-edge-strong">
      <FooterStat label={t("footer.chain")}>
        <Link2 className="size-3.5 text-foreground-muted" />
        <span>{chain}</span>
      </FooterStat>
      <FooterStat label={t("footer.cost")}>
        {(coins > 0 || tokenCosts.length === 0) && (
          <>
            <CoinIcon size={14} />
            <span>{coins > 0 ? formatEvoCost(coins) : t("common:free")}</span>
          </>
        )}
        {tokenCosts.map((token) => (
          <span
            key={token.eventTokenId}
            className="flex items-center gap-x-1 [&:not(:first-child)]:ml-1"
          >
            <img
              className="size-3.5 object-contain"
              src={token.iconUrl}
              alt={token.name}
            />
            <span>{formatEvoCost(token.amount)}</span>
          </span>
        ))}
      </FooterStat>
      {games !== undefined && (
        <FooterStat label={t("footer.games")}>
          <img
            className="size-3.5"
            src={`${assetsBaseUrl}/misc/ea/ball.png`}
            alt=""
          />
          <span>{games}</span>
        </FooterStat>
      )}
    </div>
  );
};
